import React from 'react';
import Header from '../../components/ui/Header';
import BetaBanner from './components/BetaBanner';
import ComingSoonHero from './components/ComingSoonHero';
import ComingSoonStageCard from './components/ComingSoonStageCard';
import LaunchProgress from './components/LaunchProgress';
import WaitlistForm from './components/WaitlistForm';

const stages = [
  {
    title: 'Create Your Stack',
    description:
      'Describe your shopping stack using our guided form. Choose your category, set your budget, input what matters to you, and specify your timeline so we can gather you options that meet your needs.',
    icon: 'FileText',
    accentColor: '#F58A54',
    bgGradient: 'linear-gradient(135deg, #F58A54 0%, #F06F6B 100%)',
    videoEmbed: 'https://www.loom.com/embed/dec9b708ff704a30ab0c5846b42ec4c4',
    miniSteps: [
      { icon: 'ClipboardList', label: 'Fill Form' },
      { icon: 'Camera', label: 'Add Photos' },
      { icon: 'DollarSign', label: 'Set Budget' },
    ],
    stats: [
      { value: '5 min', label: 'Avg. setup' },
      { value: '100%', label: 'Free to post' },
      { value: '24/7', label: 'Available' },
    ],
    features: [
      'AI-assisted Stack description to capture all requirements',
      'Photo upload for visual context — up to 20 images per Stack',
      'Budget range and timeline preferences guide vendor matching',
      'Category-specific questionnaires ensure nothing is missed',
    ],
    detail:
      "Our AI-powered smart form adapts to your Stack category, asking the right questions for your specific needs. Whether it's a car purchase, kitchen renovation, or a new laptop, Stackd's AI helps you articulate requirements so you get exactly what you need, at the best price, every time.",
  },
  {
    title: 'Connect with Vendors',
    description:
      'Stackd matches you with verified, relevant vendors either online (for products) or in your area (for services). We auto match you with the best online options, and for services, vendors will review your detailed brief and submit competitive quotes — you stay in control of who you hear from, when, and how.',
    icon: 'Users',
    accentColor: '#F06F6B',
    bgGradient: 'linear-gradient(135deg, #F06F6B 0%, #E0528A 100%)',
    videoEmbed: 'https://www.loom.com/embed/0b429cabc4544547934b1d98d78b17e6',
    miniSteps: [
      { icon: 'Search', label: 'Match' },
      { icon: 'Send', label: 'Invite' },
      { icon: 'MessageSquare', label: 'Chat' },
    ],
    stats: [
      { value: '3,500+', label: 'Vendors' },
      { value: '72h', label: 'First quote' },
      { value: '4.8★', label: 'Avg. rating' },
    ],
    features: [
      'Automated matching based on location, category, and availability',
      'Vendor profiles with verified reviews, past work, and credentials',
      'Direct messaging to clarify Stack details before quoting',
      'Control which vendors can see and quote on your Stack',
    ],
    detail:
      "Stackd has 2 methods of building your stacks. 'Online only' stacks will utilize AI to identify as many viable options you'd like to consider for your comparison. For more custom purchases (think home renovations, cars, credit cards, loans, etc.) Stackd's vendor network is curated and verified.",
  },
  {
    title: 'Compare & Choose',
    description:
      'Receive structured quotes and compare them side-by-side on price, timeline, inclusions, and vendor reputation. Define your importance criteria, and our scoring tools help you make a confident, informed decision every time.',
    icon: 'BarChart3',
    accentColor: '#E0528A',
    bgGradient: 'linear-gradient(135deg, #E0528A 0%, #F06F6B 100%)',
    videoEmbed: 'https://www.loom.com/embed/722ff91518714382a23105754bed72b3',
    miniSteps: [
      { icon: 'FileCheck', label: 'Receive' },
      { icon: 'SlidersHorizontal', label: 'Filter' },
      { icon: 'CheckCircle', label: 'Decide' },
    ],
    stats: [
      { value: '3–8', label: 'Avg. quotes' },
      { value: '23%', label: 'Avg. savings' },
      { value: '1 click', label: 'Accept quote' },
    ],
    features: [
      'Structured quote format makes apples-to-apples comparison easy',
      'Weighted scoring system you can customize by priority',
      'Price, timeline, inclusions, and reputation all in one view',
      'One-click quote acceptance and vendor notification',
    ],
    detail:
      "Our comparison engine normalizes quotes into a consistent format so you're never comparing apples to oranges. Vendors must submit in the format you specify, putting you back in the driver's seat. Adjust weighting sliders to prioritize what matters most — price, speed, or vendor track record.",
  },
  {
    title: 'Buy',
    description:
      "Once you've chosen a vendor, Stackd keeps your Stack on track with milestone management, payment scheduling, direct communication, and the final purchase — all in one place.",
    icon: 'CheckCircle',
    accentColor: '#F58A54',
    bgGradient: 'linear-gradient(135deg, #F58A54 0%, #F06F6B 100%)',
    miniSteps: [
      { icon: 'Calendar', label: 'Milestones' },
      { icon: 'CreditCard', label: 'Payments' },
      { icon: 'Bell', label: 'Updates' },
    ],
    stats: [
      { value: '94%', label: 'On-time rate' },
      { value: 'Real-time', label: 'Updates' },
      { value: 'Secure', label: 'Payments' },
    ],
    features: [
      'Milestone-based Stack timeline with automated reminders',
      'Secure payment releases tied to milestone completion',
      'Real-time notifications for every Stack update',
      'Dispute resolution support if issues arise',
    ],
    detail:
      "Stackd's milestone tracker breaks your Stack into clear phases with agreed deliverables. Payments are held securely and released only when milestones are marked complete and approved — giving both buyers and vendors confidence throughout the Stack lifecycle.",
  },
];

const ComingSoonLanding = () => {
  return (
    <div className="min-h-screen bg-background">
      <BetaBanner />
      <Header />
      <main>
        <ComingSoonHero />

        {/* Stages */}
        <section className="py-20 bg-background">
          <div className="max-w-6xl mx-auto px-4 lg:px-8">
            <div className="text-center mb-16">
              <p className="text-lg font-semibold mb-6" style={{ color: '#F06F6B' }}>
                <em>The average household saves $5,482 per year by using Stackd</em>
              </p>
              <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-4">
                Four steps to success
              </h2>
              <p className="text-text-secondary text-lg max-w-2xl mx-auto">
                Stackd guides you through every phase of your purchase.
              </p>
              {/* Beta note */}
              <div className="mt-6 inline-flex items-center gap-2 px-4 py-2 rounded-xl bg-amber-50 border border-amber-200 text-amber-700 text-sm font-medium">
                <span>🚧</span>
                <span>These features are in active development. Some may not be available at launch.</span>
              </div>
            </div>

            <div className="space-y-24">
              {stages?.map((stage, index) => (
                <ComingSoonStageCard
                  key={index}
                  stage={stage}
                  index={index}
                  isReversed={index % 2 !== 0}
                />
              ))}
            </div>
          </div>
        </section>

        <LaunchProgress />
        <WaitlistForm />
      </main>
    </div>
  );
};

export default ComingSoonLanding;
